/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Pill, Activity, Heart, Utensils, Wind, Moon, AlertCircle,
  Clock, Loader2, ChevronRight, Sparkles,
} from 'lucide-react';

// ── Subscriber-safe state copy ──────────────────────────────────
const STATE_COPY = {
  stable_on_day:               'Your response pattern looks stable today. Continue today’s plan.',
  delayed_on:                  'Medicine response appears delayed. Please wait and log when ON starts.',
  no_on_risk:                  'Medicine effect is not appearing as expected. Please contact your care team.',
  early_off:                   'OFF started earlier than expected. Please log the next medicine and symptoms carefully.',
  peak_dyskinesia:             'High movement activity noted. Avoid adding extra ReGenflō items unless your care team advises.',
  stress_off:                  'Stress or activity may be affecting your movement today. Reduce pressure if possible and keep logging.',
  meal_interference_day:       'Food may have affected medicine response. Please avoid heavy/protein/fat food before the next dose.',
  gut_positive_motor_neutral:  'Gut response looks improved and movement response is stable.',
  gut_positive_motor_negative: 'Gut response improved but movement response needs review. Your care team will check this.',
  calibration_intolerance:     'Your care team needs to review today’s pattern before the next change.',
};

// ── Status pill ─────────────────────────────────────────────────
function statusFromGate(gate) {
  if (gate === 'severe_red') return { label: 'Review needed', cls: 'bg-rose-100 text-rose-700 border-rose-300' };
  if (gate === 'red')        return { label: 'Hold',          cls: 'bg-rose-50  text-rose-700 border-rose-200' };
  if (gate === 'yellow')     return { label: 'Watch',         cls: 'bg-amber-50 text-amber-700 border-amber-200' };
  if (gate === 'green')      return { label: 'Stable',        cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  return { label: 'Today’s plan', cls: 'bg-stone-100 text-stone-700 border-stone-200' };
}

// Time helper
function fmtTime(t) {
  if (!t) return '';
  if (typeof t === 'string' && t.length <= 5) return t;
  try {
    return new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return t;
  }
}

// ── Big buttons config ──────────────────────────────────────────
const MEAL_TYPES = [
  ['light', 'Light'], ['moderate', 'Moderate'], ['heavy', 'Heavy'],
  ['protein', 'High protein'], ['fatty', 'Fatty'],
  ['outside', 'Outside food'], ['cheese', 'Cheese'],
];
const STRESS_TYPES = [
  ['meeting', 'Meeting'], ['travel', 'Travel'], ['social_pressure', 'Social pressure'],
  ['work_pressure', 'Work pressure'], ['function', 'Function/event'], ['none', 'No major stress'],
];
const BOWEL_NOTES = [
  ['normal', 'Normal'], ['constipation', 'Constipation'],
  ['gas', 'Gas/heaviness'], ['done', 'Bowel done'],
];

export default function SubscriberNeuroFlowTab({ subscriberId }) {
  const [today, setToday] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [openCategory, setOpenCategory] = useState(null);

  const reload = useCallback(async () => {
    if (!subscriberId) return;
    setLoading(true);
    setPageError(null);
    try {
      const [todayR, evR] = await Promise.all([
        axios.get(`${API}/neuroflow/${subscriberId}/today`),
        axios.get(`${API}/neuroflow/${subscriberId}/events?limit=30`),
      ]);
      setToday(todayR.data);
      setEvents(evR.data?.items || []);
    } catch (e) {
      setPageError(e?.response?.data?.detail || 'Could not load NeuroFlow.');
    } finally {
      setLoading(false);
    }
  }, [subscriberId]);
  useEffect(() => { reload(); }, [reload]);

  const logEvent = useCallback(async (payload, successMsg) => {
    try {
      const r = await axios.post(`${API}/neuroflow/${subscriberId}/events`, payload);
      if (successMsg) toast.success(successMsg);
      // Engine result is included; refresh the panel.
      if (r.data?.engine?.skipped === false) {
        // Reload to pick up new snapshot/instructions.
        reload();
      } else {
        // Even when engine doesn't run (e.g. non-state-affecting event), refresh events list.
        reload();
      }
      setOpenCategory(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save. Please retry.');
    }
  }, [subscriberId, reload]);

  const snap = today?.snapshot;
  const status = statusFromGate(snap?.gate_status);
  const moduleActive = today?.module_active === true;

  // Phase D polish — compute "last logged X min ago" per button category.
  // Uses cached events only. No new endpoint.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const lastLoggedByCategory = useMemo(() => {
    const out = {};
    const push = (key, t) => {
      if (!t) return;
      const ms = new Date(t).getTime();
      if (Number.isNaN(ms)) return;
      if (!out[key] || ms > out[key]) out[key] = ms;
    };
    for (const ev of events || []) {
      const t = ev.event_time;
      switch (ev.event_type) {
        case 'medicine_taken':          push('medicine_taken', t); break;
        case 'on_started':              push('on_started', t); break;
        case 'on_quality':               push('on_good', t); break;
        case 'wearing_off_started':     push('wearing_off', t); break;
        case 'full_off':                push('full_off', t); break;
        case 'dyskinesia_started':
        case 'dyskinesia_score':        push('dyskinesia', t); break;
        case 'meal_taken':              push('meal', t); break;
        case 'stress_activity':         push('stress', t); break;
        case 'bowel_done':              push('bowel', t); break;
        case 'sleep_quality':
        case 'morning_state':           push('sleep', t); break;
        case 'unusual_internal_signal': push('unusual', t); break;
        default: break;
      }
    }
    return out;
  }, [events]);

  const formatAgo = useCallback((ms) => {
    if (!ms) return null;
    const diffMin = Math.max(0, Math.round((nowTick - ms) / 60000));
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    if (h < 6) return m === 0 ? `${h}h ago` : `${h}h ${m}m ago`;
    return `${h}h ago`;
  }, [nowTick]);

  const lastAgoFor = useCallback((key) => {
    const ms = lastLoggedByCategory[key];
    return ms ? formatAgo(ms) : null;
  }, [lastLoggedByCategory, formatAgo]);

  const nextMedicine = useMemo(() => {
    if (!today?.anchors?.length) return null;
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const cur = `${hh}:${mm}`;
    const upcoming = today.anchors.find(a => (a.planned_time || '') > cur);
    return upcoming || today.anchors[0];
  }, [today]);

  const nextRegenflo = useMemo(() => {
    const items = today?.schedule_version?.schedule_items || [];
    return items.find(it => it.item_type === 'regenflo_item' && it.status === 'planned')
      || items.find(it => it.item_type === 'regenflo_item' && it.status === 'moved');
  }, [today]);

  const heldRegenflo = useMemo(() => {
    const items = today?.schedule_version?.schedule_items || [];
    return items.find(it => it.item_type === 'regenflo_item' && it.status === 'held');
  }, [today]);

  // Safety message for red/severe-red alerts
  const hasUrgent = today?.open_alerts?.some(a => a.severity === 'red' || a.severity === 'critical');

  if (loading) {
    return (
      <div data-testid="subscriber-neuroflow-loading" className="text-center py-10 text-[#8A8681]">
        <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" /> Loading…
      </div>
    );
  }

  if (pageError) {
    return (
      <div
        data-testid="subscriber-neuroflow-error"
        className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center"
      >
        <AlertCircle className="w-5 h-5 mx-auto text-amber-600 mb-2" />
        <div className="text-sm text-amber-900">{pageError}</div>
        <Button size="sm" variant="outline" className="mt-3 border-amber-300" onClick={reload}>
          Retry
        </Button>
      </div>
    );
  }

  if (!moduleActive) {
    return (
      <div
        data-testid="subscriber-neuroflow-inactive"
        className="rounded-2xl border border-[#D8D4CE] bg-[#F4F2EE] p-8 text-center"
      >
        <Sparkles className="w-8 h-8 mx-auto text-[#8A8681] mb-3" strokeWidth={1.5} />
        <div className="text-sm text-[#5C5955] font-['Work_Sans']">
          NeuroFlow is not active for your account yet.
        </div>
        <div className="text-xs text-[#8A8681] mt-2">
          Your care team will activate it when ready.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="subscriber-neuroflow-tab" className="space-y-6">
      {/* ── Header card ───────────────────────────────────────── */}
      <div className="rounded-2xl border border-[#D8D4CE] bg-[#F4F2EE] p-6">
        <div className="flex items-center gap-2 mb-2">
          <span
            data-testid="nf-status-pill"
            className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${status.cls}`}
          >
            {status.label}
          </span>
        </div>
        <h2 className="text-xl font-['Outfit'] text-[#2C2A29] mb-1">Today’s NeuroFlow Plan</h2>

        <div data-testid="nf-current-instruction" className="text-sm text-[#5C5955] font-['Work_Sans'] leading-relaxed mt-2">
          {STATE_COPY[snap?.current_state] || 'Continue today’s plan and keep logging events as they happen.'}
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <NextItem
            testId="nf-next-medicine"
            icon={<Pill className="w-4 h-4 text-[#5C6B8A]" strokeWidth={1.5} />}
            label="Next medicine"
            primary={nextMedicine ? `${fmtTime(nextMedicine.planned_time)} · ${nextMedicine.medicine_name}` : 'No anchors set'}
            secondary={nextMedicine?.dose_label || nextMedicine?.strength_label || ''}
          />
          {heldRegenflo ? (
            <NextItem
              testId="nf-held-regenflo"
              icon={<AlertCircle className="w-4 h-4 text-[#8C6239]" strokeWidth={1.5} />}
              label="ReGenflō item — held today"
              primary="Held"
              secondary={heldRegenflo.reason || 'Held by your care team’s rules.'}
              warm
            />
          ) : nextRegenflo ? (
            <NextItem
              testId="nf-next-regenflo"
              icon={<Sparkles className="w-4 h-4 text-[#4A5D4E]" strokeWidth={1.5} />}
              label="Next ReGenflō item"
              primary={fmtTime(nextRegenflo.adjusted_time || nextRegenflo.planned_time) || 'Today'}
              secondary={nextRegenflo.status === 'moved'
                ? 'Moved a little later today.' : 'On schedule.'}
            />
          ) : null}
        </div>
      </div>

      {/* ── Safety banner for red / severe-red ────────────────── */}
      {hasUrgent && (
        <div
          data-testid="nf-safety-banner"
          className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-4 flex items-start gap-3"
        >
          <AlertCircle className="w-5 h-5 text-rose-700 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-rose-900 font-['Work_Sans']">
            Please contact your care team. Do not make medicine changes on your own.
          </div>
        </div>
      )}

      {/* ── Big event buttons ─────────────────────────────────── */}
      <div data-testid="nf-buttons-grid" className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <BigButton
          testId="nf-btn-medicine-taken"
          icon={<Pill className="w-5 h-5" />}
          label="Medicine taken"
          lastLogged={lastAgoFor('medicine_taken')}
          onClick={() => logEvent({ event_type: 'medicine_taken' }, 'Medicine logged.')}
        />
        <BigButton
          testId="nf-btn-on-started"
          icon={<Activity className="w-5 h-5" />}
          label="ON started"
          lastLogged={lastAgoFor('on_started')}
          onClick={() => logEvent({ event_type: 'on_started' }, 'ON started — saved.')}
        />
        <BigButton
          testId="nf-btn-on-good"
          icon={<Heart className="w-5 h-5" />}
          label="ON is good"
          lastLogged={lastAgoFor('on_good')}
          onClick={() => logEvent({ event_type: 'on_quality', quality_score: 8 }, 'ON quality saved.')}
        />
        <BigButton
          testId="nf-btn-wearing-off"
          icon={<Activity className="w-5 h-5" />}
          label="Wearing OFF"
          lastLogged={lastAgoFor('wearing_off')}
          onClick={() => logEvent({ event_type: 'wearing_off_started' }, 'Wearing OFF logged.')}
        />
        <BigButton
          testId="nf-btn-full-off"
          icon={<Activity className="w-5 h-5" />}
          label="Full OFF"
          lastLogged={lastAgoFor('full_off')}
          onClick={() => logEvent({ event_type: 'full_off' }, 'Full OFF logged.')}
        />
        <BigButton
          testId="nf-btn-dyskinesia"
          icon={<Sparkles className="w-5 h-5" />}
          label="Dyskinesia"
          lastLogged={lastAgoFor('dyskinesia')}
          onClick={() => setOpenCategory(openCategory === 'dyskinesia' ? null : 'dyskinesia')}
        />
        <BigButton
          testId="nf-btn-meal"
          icon={<Utensils className="w-5 h-5" />}
          label="Meal"
          lastLogged={lastAgoFor('meal')}
          onClick={() => setOpenCategory(openCategory === 'meal' ? null : 'meal')}
        />
        <BigButton
          testId="nf-btn-stress"
          icon={<Wind className="w-5 h-5" />}
          label="Stress / activity"
          lastLogged={lastAgoFor('stress')}
          onClick={() => setOpenCategory(openCategory === 'stress' ? null : 'stress')}
        />
        <BigButton
          testId="nf-btn-bowel"
          icon={<Heart className="w-5 h-5" />}
          label="Bowel"
          lastLogged={lastAgoFor('bowel')}
          onClick={() => setOpenCategory(openCategory === 'bowel' ? null : 'bowel')}
        />
        <BigButton
          testId="nf-btn-sleep"
          icon={<Moon className="w-5 h-5" />}
          label="Sleep / morning"
          lastLogged={lastAgoFor('sleep')}
          onClick={() => setOpenCategory(openCategory === 'sleep' ? null : 'sleep')}
        />
        <BigButton
          testId="nf-btn-unusual"
          icon={<AlertCircle className="w-5 h-5" />}
          label="Something unusual"
          lastLogged={lastAgoFor('unusual')}
          onClick={() => setOpenCategory(openCategory === 'unusual' ? null : 'unusual')}
        />
      </div>

      {/* ── Sub-forms ─────────────────────────────────────────── */}
      {openCategory === 'dyskinesia' && (
        <ScoreForm
          testId="nf-form-dyskinesia"
          title="Dyskinesia score"
          onSubmit={(score, note) => logEvent(
            { event_type: 'dyskinesia_score', severity_score: score, note: note || undefined },
            'Dyskinesia logged.',
          )}
          onCancel={() => setOpenCategory(null)}
        />
      )}
      {openCategory === 'meal' && (
        <ChipsForm
          testId="nf-form-meal"
          title="Meal type"
          options={MEAL_TYPES}
          onPick={(value) => logEvent(
            { event_type: 'meal_taken', meal_type: value },
            'Meal logged.',
          )}
          onCancel={() => setOpenCategory(null)}
        />
      )}
      {openCategory === 'stress' && (
        <ChipsForm
          testId="nf-form-stress"
          title="Stress / activity"
          options={STRESS_TYPES}
          onPick={(value) => logEvent(
            { event_type: 'stress_activity', stress_type: value },
            'Stress logged.',
          )}
          onCancel={() => setOpenCategory(null)}
        />
      )}
      {openCategory === 'bowel' && (
        <ChipsForm
          testId="nf-form-bowel"
          title="Bowel"
          options={BOWEL_NOTES}
          onPick={(value) => logEvent(
            { event_type: 'bowel_done', note: value },
            'Bowel logged.',
          )}
          onCancel={() => setOpenCategory(null)}
        />
      )}
      {openCategory === 'sleep' && (
        <SleepForm
          testId="nf-form-sleep"
          onSubmit={(sleep, stiffness) => Promise.all([
            sleep != null && logEvent(
              { event_type: 'sleep_quality', quality_score: sleep },
              'Sleep saved.',
            ),
            stiffness != null && logEvent(
              { event_type: 'morning_state', severity_score: stiffness },
              'Morning state saved.',
            ),
          ])}
          onCancel={() => setOpenCategory(null)}
        />
      )}
      {openCategory === 'unusual' && (
        <ScoreForm
          testId="nf-form-unusual"
          title="Something unusual inside"
          onSubmit={(score, note) => logEvent(
            { event_type: 'unusual_internal_signal', severity_score: score, note: note || undefined },
            'Saved. Your care team will see this.',
          )}
          onCancel={() => setOpenCategory(null)}
        />
      )}

      {/* ── Today's history ──────────────────────────────────── */}
      <div data-testid="nf-event-history" className="rounded-2xl border border-[#D8D4CE] bg-white p-5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] font-['Work_Sans'] mb-3">
          Today’s log
        </div>
        {events.length === 0 ? (
          <div className="text-sm text-[#8A8681] py-2">No events logged yet today.</div>
        ) : (
          <ul className="divide-y divide-[#EBE8E3]">
            {events.slice(0, 12).map((e) => (
              <li key={e.id} className="py-2 flex items-start gap-3 text-sm">
                <span className="text-xs text-[#8A8681] font-mono w-20 flex-shrink-0">{fmtTime(e.event_time)}</span>
                <div className="flex-1">
                  <div className="text-[#2C2A29]">{labelForEvent(e)}</div>
                  {e.note && <div className="text-xs text-[#8A8681] mt-0.5">{e.note}</div>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Subscriber-friendly event labels
function labelForEvent(e) {
  switch (e.event_type) {
    case 'medicine_taken':         return 'Medicine taken';
    case 'on_started':             return 'ON started';
    case 'on_quality':             return e.quality_score >= 7 ? 'ON is good' : 'ON quality logged';
    case 'wearing_off_started':    return 'Wearing OFF started';
    case 'full_off':               return 'Full OFF';
    case 'dyskinesia_started':     return 'Dyskinesia started';
    case 'dyskinesia_score':       return `Dyskinesia · score ${e.severity_score ?? '—'}`;
    case 'meal_taken':             return `Meal · ${e.meal_type || 'logged'}`;
    case 'stress_activity':        return `Stress · ${e.stress_type || 'logged'}`;
    case 'bowel_done':             return `Bowel · ${e.note || 'logged'}`;
    case 'sleep_quality':          return `Sleep · ${e.quality_score ?? '—'}/10`;
    case 'morning_state':          return `Morning stiffness · ${e.severity_score ?? '—'}/10`;
    case 'unusual_internal_signal':return `Unusual signal · ${e.severity_score ?? '—'}/10`;
    default:                       return e.event_type;
  }
}

// ── Sub components ─────────────────────────────────────────────
function NextItem({ testId, icon, label, primary, secondary, warm }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border p-3 ${warm ? 'border-amber-200 bg-amber-50/40' : 'border-[#EBE8E3] bg-white'}`}
    >
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681]">{label}</span>
      </div>
      <div className="text-sm text-[#2C2A29] font-['Work_Sans']">{primary}</div>
      {secondary && <div className="text-xs text-[#8A8681] mt-0.5">{secondary}</div>}
    </div>
  );
}

function BigButton({ testId, icon, label, onClick, lastLogged }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="rounded-2xl border-2 border-[#D8D4CE] bg-white hover:border-[#4A5D4E] hover:bg-[#F4F2EE] transition-all duration-200 p-4 min-h-[88px] flex flex-col items-center justify-center gap-2 text-center group"
    >
      <span className="text-[#4A5D4E] group-hover:scale-110 transition-transform">{icon}</span>
      <span className="text-xs font-['Work_Sans'] text-[#2C2A29] leading-tight">{label}</span>
      {lastLogged && (
        <span
          data-testid={`${testId}-last-logged`}
          className="text-[10px] font-['Work_Sans'] text-[#4A5D4E] bg-[#E8ECE6] px-2 py-0.5 rounded-full"
        >
          Logged {lastLogged}
        </span>
      )}
    </button>
  );
}

function ScoreForm({ testId, title, onSubmit, onCancel }) {
  const [score, setScore] = useState(5);
  const [note, setNote] = useState('');
  return (
    <div data-testid={testId} className="rounded-2xl border-2 border-[#4A5D4E] bg-white p-5">
      <div className="text-sm text-[#2C2A29] font-['Outfit'] mb-3">{title}</div>
      <input
        data-testid={`${testId}-score`}
        type="range" min="0" max="10" value={score}
        onChange={(e) => setScore(Number(e.target.value))}
        className="w-full"
      />
      <div className="text-center text-2xl font-['Outfit'] text-[#4A5D4E] mb-3">{score}</div>
      <textarea
        data-testid={`${testId}-note`}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="Optional note…"
        className="w-full bg-[#F4F2EE] border border-[#D8D4CE] rounded-lg px-3 py-2 text-xs text-[#2C2A29] mb-3"
      />
      <div className="flex gap-2 justify-end">
        <Button data-testid={`${testId}-cancel`} variant="outline" size="sm" className="border-[#D8D4CE]" onClick={onCancel}>Cancel</Button>
        <Button data-testid={`${testId}-save`} size="sm" className="bg-[#4A5D4E] hover:bg-[#3D4F40]" onClick={() => onSubmit(score, note)}>Save</Button>
      </div>
    </div>
  );
}

function ChipsForm({ testId, title, options, onPick, onCancel }) {
  return (
    <div data-testid={testId} className="rounded-2xl border-2 border-[#4A5D4E] bg-white p-5">
      <div className="text-sm text-[#2C2A29] font-['Outfit'] mb-3">{title}</div>
      <div className="flex flex-wrap gap-2 mb-3">
        {options.map(([value, label]) => (
          <button
            key={value}
            data-testid={`${testId}-${value}`}
            onClick={() => onPick(value)}
            className="px-4 py-2 rounded-full border border-[#D8D4CE] bg-[#F4F2EE] hover:bg-[#4A5D4E] hover:text-white hover:border-[#4A5D4E] text-sm text-[#2C2A29] transition-colors"
          >
            {label}
          </button>
        ))}
      </div>
      <div className="flex justify-end">
        <Button data-testid={`${testId}-cancel`} variant="outline" size="sm" className="border-[#D8D4CE]" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

function SleepForm({ testId, onSubmit, onCancel }) {
  const [sleep, setSleep] = useState(7);
  const [stiff, setStiff] = useState(3);
  return (
    <div data-testid={testId} className="rounded-2xl border-2 border-[#4A5D4E] bg-white p-5 space-y-4">
      <div>
        <div className="text-sm text-[#2C2A29] font-['Outfit'] mb-2">Sleep quality (0–10)</div>
        <input data-testid={`${testId}-sleep`} type="range" min="0" max="10" value={sleep} onChange={(e) => setSleep(Number(e.target.value))} className="w-full" />
        <div className="text-center text-xl font-['Outfit'] text-[#4A5D4E]">{sleep}</div>
      </div>
      <div>
        <div className="text-sm text-[#2C2A29] font-['Outfit'] mb-2">Morning stiffness (0–10)</div>
        <input data-testid={`${testId}-stiff`} type="range" min="0" max="10" value={stiff} onChange={(e) => setStiff(Number(e.target.value))} className="w-full" />
        <div className="text-center text-xl font-['Outfit'] text-[#4A5D4E]">{stiff}</div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button data-testid={`${testId}-cancel`} variant="outline" size="sm" className="border-[#D8D4CE]" onClick={onCancel}>Cancel</Button>
        <Button data-testid={`${testId}-save`} size="sm" className="bg-[#4A5D4E] hover:bg-[#3D4F40]" onClick={() => onSubmit(sleep, stiff)}>Save</Button>
      </div>
    </div>
  );
}
